// Mapper puro: visão do carrinho nativo (NativeCartView, devolvida por
// GET /api/cart/native em `view` e pelas rotas de item em `cart`) -> o tipo
// Cart que a vitrine já consome (types/cart.ts, hoje alimentado pelo
// normalizeCart de services/woocommerce/cart.ts).
//
// Pode rodar no cliente: sem "server-only", sem banco, e o único import de
// nativeCartView.ts é de tipo (apagado na compilação). Mesmas convenções do
// normalizeCart do Woo:
//  - CartMoney.value: unidades mínimas como string de dígitos;
//  - CartItem.price/total e Cart.subtotal: valor em reais (number);
//  - maxQuantity limitado a 999 (999 quando não há limite de estoque).
//
// Ids: item.key = productVariantId (é o que PATCH/DELETE
// /api/cart/native/items/[variantId] recebem); item.id/productId = id Woo do
// produto pai; variationId = id Woo da variação, só quando difere do pai.
//
// O carrinho nativo não tem taxas nesta etapa: needsShipping = true,
// shippingPackages vazio e totals.price = subtotal dos itens menos o
// desconto do cupom nativo (quando houver, 2026-10-16). A
// estimativa de frete do carrinho (quando ligada) é aplicada depois, no
// provider, por applyNativeShippingEstimate (nativeCartClient.ts).
//
// permalink/slug usam products.slug. Para os poucos produtos cujo slug Woo
// tinha percent-encoding, a rota pública redireciona o slug nativo
// normalizado para o slug do Woo (services/catalog/nativeSlugRedirect.ts).

import type { Cart, CartItem, CartMoney } from "../../types/cart.ts";
import type { NativeCartView, NativeCartViewItem } from "./nativeCartView.ts";
import { getProductHref } from "../routing/storefrontUrls.ts";
import { stripHtml } from "../../services/woocommerce/mappers.ts";

const CURRENCY_MINOR_UNIT = 2;
const MAX_LINE_QUANTITY = 999;
const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = { BRL: "R$" };
const DIGITS = /^\d+$/;

function minorToMajor(value: string | null): number {
  return value !== null && DIGITS.test(value) ? Number(value) / 10 ** CURRENCY_MINOR_UNIT : 0;
}

function money(value: string, currencyCode: string, currencySymbol: string): CartMoney {
  return {
    value: DIGITS.test(value) ? value : "0",
    currencyCode,
    currencySymbol,
    currencyMinorUnit: CURRENCY_MINOR_UNIT,
  };
}

function mapItem(item: NativeCartViewItem): CartItem {
  const parentId = item.wooParentProductId ?? item.wooProductId ?? 0;
  const name = stripHtml(item.name);
  return {
    key: item.productVariantId,
    id: parentId,
    productId: parentId,
    variationId: item.wooProductId !== null && item.wooProductId !== parentId ? item.wooProductId : undefined,
    name,
    sku: item.sku ?? undefined,
    permalink: item.slug ? getProductHref(item.slug) : undefined,
    slug: item.slug ?? undefined,
    variation: item.variantLabel
      ? [{ attribute: "variacao", label: "Variação", value: stripHtml(item.variantLabel) }]
      : [],
    quantity: item.quantity,
    minQuantity: 1,
    maxQuantity: item.maxQuantity === null ? MAX_LINE_QUANTITY : Math.min(MAX_LINE_QUANTITY, item.maxQuantity),
    quantityStep: 1,
    image: item.imageUrl ? { src: item.imageUrl, alt: stripHtml(item.imageAlt || item.name) } : undefined,
    price: minorToMajor(item.unitPriceMinor),
    total: minorToMajor(item.lineTotalMinor),
    freeShipping: item.freeShipping,
    ...(item.orderBumpDiscountMinor && item.orderBumpDiscountMinor !== "0" ? { orderBumpDiscount: minorToMajor(item.orderBumpDiscountMinor) } : {}),
  };
}

export function nativeCartToCart(view: NativeCartView): Cart {
  const currencyCode = view.currency;
  const currencySymbol = CURRENCY_SYMBOLS[currencyCode] ?? currencyCode;
  const zero = money("0", currencyCode, currencySymbol);
  const items = money(view.itemsSubtotalMinor, currencyCode, currencySymbol);
  // Cupom nativo (só com NATIVE_COUPONS=on o servidor manda `coupon`): o
  // desconto nos produtos entra em totals.discount e sai de totals.price; o
  // frete grátis só é aplicado no checkout, com o frete escolhido.
  const coupon = view.coupon ?? null;
  const itemsMinor = DIGITS.test(view.itemsSubtotalMinor) ? BigInt(view.itemsSubtotalMinor) : BigInt(0);
  const rawDiscount = coupon?.applied && DIGITS.test(coupon.itemDiscountMinor) ? BigInt(coupon.itemDiscountMinor) : BigInt(0);
  const discountMinor = rawDiscount > itemsMinor ? itemsMinor : rawDiscount;
  const discount = money(discountMinor.toString(), currencyCode, currencySymbol);
  return {
    items: view.items.map(mapItem),
    itemsCount: view.itemsCount,
    subtotal: minorToMajor(view.itemsSubtotalMinor),
    currencyCode,
    currencySymbol,
    currencyMinorUnit: CURRENCY_MINOR_UNIT,
    coupons: coupon
      ? [{ code: coupon.code, discountType: coupon.kind, totalDiscount: discount, totalDiscountTax: zero, applied: coupon.applied, message: coupon.message, freeShipping: coupon.freeShipping }]
      : [],
    nativeCoupon: coupon,
    fees: [],
    taxLines: [],
    totals: {
      items,
      itemsTax: zero,
      fees: zero,
      feesTax: zero,
      discount,
      discountTax: zero,
      shipping: zero,
      shippingTax: zero,
      price: money((itemsMinor - discountMinor).toString(), currencyCode, currencySymbol),
      tax: zero,
    },
    needsShipping: true,
    hasCalculatedShipping: false,
    shippingPackages: [],
  };
}

// O tipo Cart não tem campo de disponibilidade por item; a vitrine usa isto
// para sinalizar (e bloquear o checkout de) itens que não podem seguir agora
// -- sem preço ativo, sem estoque ou produto despublicado.
export function getUnavailableNativeCartItemKeys(view: NativeCartView): string[] {
  return view.items.filter((item) => !item.available).map((item) => item.productVariantId);
}
